import React, { useRef, useEffect, useState } from 'react';
import styles from './YoutubePlayer.module.css';
import { Spinner } from 'react-bootstrap';

interface YoutubePlayerProps {
  videoId: string;
  targetTime: number | undefined;
  isPlaying: boolean;
  showControls: boolean;
  onUserPlay: () => void;
  onUserPause: () => void;
  onTimeUpdate: (time: number) => void;
  volume: number;
  onPlayerStateChange?: (state: YT.PlayerState) => void;
  minimapZoom?: boolean;
  videoQuality?: '720p' | '1080p' | '1440p' | '4k';
}

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

const DESIRED_ZOOM_LEVEL = 2.0;  // How much we want to zoom in

const getVideoDimensions = (quality: string) => {
  switch (quality) {
    case '720p':
      return { width: 1280, height: 720 };
    case '1080p':
      return { width: 1920, height: 1080 };
    case '1440p':
      return { width: 2560, height: 1440 };
    case '4k':
      return { width: 3840, height: 2160 };
    default:
      return { width: 1920, height: 1080 };
  }
};

const YoutubePlayer: React.FC<YoutubePlayerProps> = ({ 
  videoId, 
  targetTime, 
  isPlaying, 
  showControls, 
  onUserPlay, 
  onUserPause, 
  onTimeUpdate, 
  volume, 
  onPlayerStateChange, 
  minimapZoom = false,
  videoQuality = '1080p'
}) => {
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const animationFrameRef = useRef<number>();
  const isProgrammaticChange = useRef(false);
  const [playerState, setPlayerState] = useState<YT.PlayerState>(-1);
  const [showLoading, setShowLoading] = useState(true);
  const [scale, setScale] = useState(1);

  const { width: YOUTUBE_WIDTH, height: YOUTUBE_HEIGHT } = getVideoDimensions(videoQuality);

  useEffect(() => {
    // Reset isReady state when component mounts
    setIsReady(false);

    const loadYouTubeAPI = () => {
      if (!window.YT) {
        // Load the IFrame Player API code asynchronously.
        const tag = document.createElement('script');
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
      }
    };

    const initPlayer = () => {
      if (containerRef.current && !playerRef.current) {
        playerRef.current = new window.YT.Player(containerRef.current, {
          height: '100%',
          width: '100%',
          videoId: videoId,
          playerVars: {
            start: Math.floor(targetTime || 0),
            autoplay: 0,
            mute: 0,
            controls: showControls ? 1 : 0,
            disablekb: 1,
            modestbranding: 1,
            rel: 0,
            showinfo: 0,
            iv_load_policy: 3,
            cc_load_policy: 3,
            fs: 0,
            autohide: 1,
            playsinline: 1,
            vq: videoQuality === '4k' ? 'hd2160' : 
                videoQuality === '1440p' ? 'hd1440' :
                videoQuality === '1080p' ? 'hd1080' :
                videoQuality === '720p' ? 'hd720' : 'hd1080',
            origin: window.location.origin,
            enablejsapi: 1,
            widget_referrer: window.location.href,
          },
          events: {
            onReady: (event: any) => {
              setIsReady(true);
            },
            onStateChange: (event: any) => {
              setPlayerState(event.data as YT.PlayerState);
              onPlayerStateChange?.(event.data as YT.PlayerState);

              playerRef.current?.unloadModule("captions");
              playerRef.current?.unloadModule("cc");
              
              if (!isProgrammaticChange.current) {
                if (event.data === YT.PlayerState.PLAYING) {
                  onUserPlay();
                } else if (event.data === YT.PlayerState.PAUSED) {
                  onUserPause();
                }
              }
              if (event.data === YT.PlayerState.PLAYING || event.data === YT.PlayerState.PAUSED) {
                isProgrammaticChange.current = false;
              }
            },
          },
        });
      }
    };

    loadYouTubeAPI();

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [videoId]); // Add videoId to the dependency array

  useEffect(() => {
    if (isReady && playerRef.current) {
      playerRef.current.setVolume(volume);
    }
  }, [volume, isReady]);

  useEffect(() => {
    if (isReady && playerRef.current && targetTime) {
      const getCurrentTime = () => {
        try {
          return playerRef.current?.getCurrentTime() || 0;
        } catch (error) {
          console.error('Error getting current time:', error);
          return 0;
        }
      };

      const currentTime = getCurrentTime();
      if (Math.abs(targetTime - currentTime) > 1) {
        playerRef.current.seekTo(targetTime, true);
      }
    }
  }, [targetTime, isReady]);

  useEffect(() => {
    if (isReady) {
      if (isPlaying && playerRef.current?.getPlayerState() === YT.PlayerState.PAUSED) {
        isProgrammaticChange.current = true;
        playerRef.current.playVideo();
      } else if (!isPlaying && playerRef.current?.getPlayerState() === YT.PlayerState.PLAYING) {
        isProgrammaticChange.current = true;
        playerRef.current.pauseVideo();
      }
    }
  }, [isPlaying, isReady]);

  useEffect(() => {
    if (playerState === -1) {
      setShowLoading(true);
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setShowLoading(false);
    }
  }, [playerState]);

  const updateTime = () => {
    if (playerRef.current && isReady && typeof playerRef.current.getCurrentTime === 'function') {
      try {
        const currentTime = playerRef.current.getCurrentTime();
        onTimeUpdate(currentTime);
      } catch (error) {
        console.error('Error getting current time:', error);
      }
    }
    animationFrameRef.current = requestAnimationFrame(updateTime);
  };

  useEffect(() => {
    if (isReady) {
      animationFrameRef.current = requestAnimationFrame(updateTime);
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isReady, onTimeUpdate]);

  const handleOverlayClick = () => {
    if (isReady && playerRef.current) {
      isProgrammaticChange.current = true;
      playerRef.current.playVideo();
      onUserPlay();
    }
  };

  // Add effect to calculate scale
  useEffect(() => {
    const calculateScale = () => {
      if (wrapperRef.current) {
        const wrapperWidth = wrapperRef.current.clientWidth;
        const wrapperHeight = wrapperRef.current.clientHeight;
        const scaleX = wrapperWidth / YOUTUBE_WIDTH;
        const scaleY = wrapperHeight / YOUTUBE_HEIGHT;
        const newScale = Math.min(scaleX, scaleY);
        setScale(newScale);
      }
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  return (
    <div className={styles.videoWrapper} ref={wrapperRef}>
      <div className={styles.videoContainer} style={{
        transform: `translate(-50%, -50%) scale(${scale})`,
        transformOrigin: 'center',
        transition: 'transform 0.3s ease-out',
        opacity: playerState === -1 ? 0 : 1,
        width: `${YOUTUBE_WIDTH}px`,
        height: `${YOUTUBE_HEIGHT}px`,
        position: 'absolute',
        top: '50%',
        left: '50%',
        willChange: 'transform',
      }}>
        <div style={{
          transform: minimapZoom ? `scale(${DESIRED_ZOOM_LEVEL})` : 'scale(1)',
          transformOrigin: 'top left',
          transition: 'transform 0.3s ease-out',
          width: '100%',
          height: '100%',
        }}>
          <div ref={containerRef} style={{
            width: '100%',
            height: '100%',
          }}></div>
        </div>
      </div>
      {playerState === -1 && (
        <div className={styles.overlay} onClick={handleOverlayClick}>
          <div className={styles.overlayText} style={{ textAlign: 'center' }}>
            {showLoading ? (
              <>
                <div className={styles.spinner}></div>
                <div style={{ 
                  color: '#aaaaaa', 
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  justifyContent: 'center'
                }}>
                  <Spinner animation="border" role="status" />
                  <span style={{ fontSize: '1rem', marginLeft: '0.5rem' }}>Loading video...</span>
                </div>
              </>
            ) : (
              <>
                <div style={{ color: '#aaaaaa', fontSize: '0.85rem' }}>Video ready</div>
                Click here to start
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default YoutubePlayer;
